import React, { useState } from 'react';
import { ShieldCheck, CheckCircle2, ArrowRight, Loader2, Sparkles } from 'lucide-react';

const ProductInquiryCard = ({ title, subtitle, productName, defaultTicket, buttonText }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    capitalSize: defaultTicket || '₹10,000 to ₹1,00,000'
  });
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMsg(null);

    try {
      const res = await fetch('/api/enroll', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: formData.name,
          userEmail: formData.email,
          userPhone: formData.phone,
          productName: productName,
          capitalSize: formData.capitalSize
        })
      });

      const data = await res.json();
      if (data.success) {
        setStatusMsg({
          type: 'success',
          text: `Inquiry for ${productName} registered! Sheet will be sent to your email.`
        });
        setFormData({ name: '', email: '', phone: '', capitalSize: defaultTicket || '' });
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Submission failed.' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Server connection error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative group">
      {/* Subtle Glow Behind the Form */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600/30 via-[#16325B]/40 to-blue-400/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition duration-500"></div>

      <div className="relative bg-[#0B1E30]/90 backdrop-blur-xl border border-slate-700/80 hover:border-blue-500/40 rounded-2xl p-6 sm:p-8 shadow-2xl transition-all duration-300">
        
        {/* Top Header Badge */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#16325B]/40 border border-blue-500/30 text-[#60A5FA] text-[11px] font-bold tracking-wider uppercase">
            <Sparkles className="w-3 h-3 text-[#60A5FA] animate-pulse" />
            Direct Allocation Desk
          </div>
          <span className="text-[10px] text-slate-400 font-mono">SEBI Regulated</span>
        </div>

        <h3 className="text-xl sm:text-2xl font-serif font-bold text-white tracking-tight">
          {title || "Request Live Inventory"}
        </h3>
        
        <p className="text-xs text-slate-300 mt-1 mb-6 leading-relaxed">
          {subtitle || "Fill your details to receive verified yield sheets and priority execution."}
        </p>

        {/* Feedback Alert */}
        {statusMsg && (
          <div
            className={`p-3.5 rounded-xl text-xs font-semibold mb-5 flex items-center gap-2.5 transition-all duration-300 ${
              statusMsg.type === 'success'
                ? 'bg-blue-900/40 text-blue-200 border border-blue-500/50 shadow-lg shadow-blue-500/10'
                : 'bg-red-500/15 text-red-300 border border-red-500/40'
            }`}
          >
            {statusMsg.type === 'success' && <CheckCircle2 className="w-4 h-4 text-[#60A5FA] shrink-0" />}
            <span>{statusMsg.text}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1.5">
              Full Legal Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Manish Kumar"
              className="w-full bg-[#081523]/90 border border-slate-700/80 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-400 focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#16325B]/40 transition-all duration-200"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1.5">
              Email Address *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="name@email.com"
              className="w-full bg-[#081523]/90 border border-slate-700/80 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-400 focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#16325B]/40 transition-all duration-200"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1.5">
              WhatsApp / Phone *
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+91 98765 43210"
              className="w-full bg-[#081523]/90 border border-slate-700/80 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-400 focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#16325B]/40 transition-all duration-200"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1.5">
              Target Commitment
            </label>
            <input
              type="text"
              value={formData.capitalSize}
              onChange={(e) => setFormData({ ...formData, capitalSize: e.target.value })}
              placeholder={`e.g. ${defaultTicket || '₹10,000 to ₹1,00,000'}`}
              className="w-full bg-[#081523]/90 border border-slate-700/80 rounded-xl px-4 py-3 text-white text-sm placeholder-slate-400 focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#16325B]/40 transition-all duration-200"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-2 py-3.5 px-6 rounded-xl bg-gradient-to-r from-[#16325B] to-[#2563EB] hover:from-[#1D4ED8] hover:to-[#3B82F6] text-white font-bold text-sm tracking-wide flex items-center justify-center gap-2 shadow-lg shadow-[#16325B]/40 hover:shadow-blue-500/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 cursor-pointer border border-blue-400/20"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-white" />
                <span>Transmitting Details...</span>
              </>
            ) : (
              <>
                <span>{buttonText || "Get Live Yield Sheet"}</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          <div className="flex items-center justify-center gap-2 pt-2 text-[10px] text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 text-[#60A5FA]" />
            <span>256-Bit Encrypted • Direct Compliance Forwarding</span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductInquiryCard;